import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { isLoggedIn } from '../../utils/auth';

// Get the base URL and auth header
const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4611/auth';

export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}`,
    prepareHeaders: (headers) => {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      if (token && isLoggedIn()) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Settings'],
  endpoints: (builder) => ({
    // Settings endpoints with pagination and search support
    getSettings: builder.query({
      query: (params = {}) => {
        // Create URL with query parameters
        const { search, page, limit, settings_type } = params;
        
        // Build query string
        const queryParams = new URLSearchParams();
        
        // Add pagination parameters if provided
        if (page) queryParams.append('page', page);
        if (limit) queryParams.append('limit', limit);
        
        // Add search parameter if provided
        if (search) queryParams.append('search', search);
        if (settings_type) queryParams.append('settings_type', settings_type);
        
        // Construct the URL with query parameters
        const queryString = queryParams.toString();
        return `/settings${queryString ? `?${queryString}` : ''}`;
      },
      providesTags: ['Settings'],
      transformResponse: (response) => {
        // Handle different possible response structures
        if (Array.isArray(response)) {
          return { 
            items: response,
            total: response.length
          };
        } else if (response.data && Array.isArray(response.data)) {
          return {
            items: response.data,
            total: response.total || response.data.length,
          };
        } else if (response.items && Array.isArray(response.items)) {
          return {
            items: response.items,
            total: response.total || response.items.length,
          };
        } else {
          console.error('Unexpected settings structure:', response);
          return { items: [], total: 0 };
        }
      },
    }),
    getSetting: builder.query({
      query: (settingId) => `/settings/${settingId}`,
      providesTags: (result, error, settingId) => [{ type: 'Settings', id: settingId }],
    }),
    createSetting: builder.mutation({
      query: (setting) => ({
        url: '/settings',
        method: 'POST',
        body: setting,
      }),
      invalidatesTags: ['Settings'],
    }),
    updateSetting: builder.mutation({
      query: ({ settingId, ...setting }) => ({
        url: `/settings/${settingId}`,
        method: 'PUT',
        body: setting,
      }),
      invalidatesTags: (result, error, { settingId }) => [
        { type: 'Settings', id: settingId },
        'Settings',
      ],
    }),
    deleteSetting: builder.mutation({
      query: (settingId) => ({
        url: `/settings/${settingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Settings'],
    }),
  }),
});

export const {
  useGetSettingsQuery,
  useGetSettingQuery,
  useCreateSettingMutation,
  useUpdateSettingMutation,
  useDeleteSettingMutation,
} = settingsApi; 